import React, { useCallback, useEffect, useState } from "react";
import reportTemplatesService from "../../../services/reportTemplatesService";

export default function ReportTemplatesPanel({
  onNew,
  onEdit,
  onView,
  refreshKey = 0,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const list = await reportTemplatesService.list();
      setItems(list);
    } catch {
      setItems([]);
      setError("Failed to load templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <div className="r6-tpl-grid-page">
      <div className="r6-tpl-grid-head">
        <div>
          <div className="r6-tpl-grid-title">Report Templates</div>
          <div className="r6-tpl-grid-sub">
            {items.length} template{items.length === 1 ? "" : "s"} — click
            a card to view, or use Edit to open the builder.
          </div>
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={onNew}
        >
          <i className="fas fa-plus" /> New Template
        </button>
      </div>

      {error && (
        <div className="error-message" role="alert">
          <i className="fas fa-exclamation-circle" /> {error}
        </div>
      )}

      {loading ? (
        <div className="r6-tpl-grid-empty">Loading templates…</div>
      ) : items.length === 0 ? (
        <div className="r6-tpl-grid-empty">
          <i className="fas fa-layer-group" />
          <h3>No templates yet</h3>
          <p>Build a reusable layout for reports.</p>
          <button type="button" className="btn btn-primary" onClick={onNew}>
            <i className="fas fa-plus" /> Create your first template
          </button>
        </div>
      ) : (
        <ul className="r6-tpl-grid">
          {items.map((tpl) => {
            const p = tpl.payload || {};
            const cols =
              p.visibleColumns && typeof p.visibleColumns === "object"
                ? Object.values(p.visibleColumns).filter(Boolean).length
                : 0;
            const chips = Array.isArray(p.activeQuickChips)
              ? p.activeQuickChips.length
              : 0;
            const hasTitle = !!String(p.title || "").trim();

            return (
              <li
                key={tpl.id}
                className="r6-tpl-tile"
                onClick={() => onView?.(tpl.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onView?.(tpl.id);
                  }
                }}
              >
                <div className="r6-tpl-tile-head">
                  <span className="r6-tpl-tile-badge">
                    <i className="fas fa-layer-group" /> Template
                  </span>
                  {tpl.is_default && (
                    <span className="r6-tpl-tile-badge is-default">
                      Default ★
                    </span>
                  )}
                </div>
                <div className="r6-tpl-tile-name" title={tpl.name}>
                  {tpl.name}
                </div>
                <div className="r6-tpl-tile-meta">
                  <span>
                    <i className="fas fa-table-columns" /> {cols || "—"} cols
                  </span>
                  {chips > 0 && (
                    <span>
                      <i className="fas fa-bolt" /> {chips} chips
                    </span>
                  )}
                  {hasTitle && (
                    <span>
                      <i className="fas fa-heading" /> has title
                    </span>
                  )}
                </div>
                <div className="r6-tpl-tile-actions">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onView?.(tpl.id);
                    }}
                    title="View template"
                  >
                    <i className="fas fa-eye" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit?.(tpl.id);
                    }}
                    title="Edit template"
                  >
                    <i className="fas fa-edit" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}